import atBreakpointChange from "../lib/at-breakpoint-change.js"
import EVENTS from "../lib/events.js"
import getMediaQuery from "../lib/media-queries.js"
import intersectionWatcher from "@/lib/intersection-watcher.js"
import SwipeNav from "../lib/swipe-navigation.js"
import srraf from "srraf"

const {
  SLIDESHOW_PAGINATION_CONNECTED,
  SLIDESHOW_PAGINATION_BULLET_CLICKED,
  SLIDESHOW_PAGINATION_SET_ACTIVE_BULLET,
  SLIDESHOW_PAGINATION_START_AUTOPLAY,
  SLIDESHOW_PAGINATION_PAUSE_AUTOPLAY,
  SLIDESHOW_PAGINATION_RESUME_AUTOPLAY,
  SLIDESHOW_PAGINATION_AUTOPLAY_ADVANCE,
  SLIDESHOW_PAGINATION_NAV_CLICK,
  SWIPE_NAV_PREV,
  SWIPE_NAV_NEXT,
  SHOPIFY_BLOCK_SELECT,
  SHOPIFY_BLOCK_DESELECT,
} = EVENTS

class SlideshowSection extends HTMLElement {
  constructor() {
    super()

    this.selectors = {
      cursor: "[js-slideshow-cursor]",
      loadingOverlay: "[js-slideshow-loading-overlay]",
      pagination: "[js-slideshow-pagination]",
      parentSection: ".shopify-section",
      slide: "[js-slideshow-slide]",
      slideByBlockId: (id) => `[data-block-id="${id}"]`,
      slideImageDesktop: "[js-slideshow-desktop-slide-image]",
      slideImageMobile: "[js-slideshow-mobile-slide-image]",
      slideContent: "[js-slideshow-slide-content]",
      slideshow: "[js-slideshow]",
      videoContainer: "[js-current-background-video-container]",
      videoPlaceholderImage:
        "[js-background-video-placeholder] .placeholder-image",
      videoPauseButton: "[js-video-pause-button]",
      videoSlide: "[js-slideshow-video-slide]",
    }

    this.classes = {
      active: "active",
      currentSlide: "current-slide",
      loaded: "loaded",
      nextSlide: "next-slide",
      prevCursor: "slideshow__cursor--prev",
      nextCursor: "slideshow__cursor--next",
      slideTransitionOut: "slide-transition-out",
      visible: "visible",
    }

    this.breakpoints = {
      mobile: getMediaQuery("mobile"),
      smallDesktop: getMediaQuery("small-desktop"),
    }

    this.strings = window.theme.strings
  }

  connectedCallback() {
    this.intersectionWatcher = intersectionWatcher(this)
    this.sectionId = this.getAttribute("data-section-id")
    this.slideshow = this.querySelector(this.selectors.slideshow)
    this.loadingOverlay = this.querySelector(this.selectors.loadingOverlay)
    this.slides = this.querySelectorAll(this.selectors.slide)
    this.slideList = Array.from(this.slides)
    this.currentSlide = this.loadingOverlay
    this.firstSlideIndex = "0"
    this.lastSlideIndex = `${this.slides.length - 1}`
    this.firstIndexOnLoad = "0"
    this.swipeNav = new SwipeNav(this)
    this.controller = new AbortController()
    this.eventOptions = { signal: this.controller.signal }
    this.blockSelected = false

    // Pagination
    window.addEventListener(
      SLIDESHOW_PAGINATION_BULLET_CLICKED(this.sectionId),
      this.handlePaginationBulletClick.bind(this),
      this.eventOptions,
    )

    window.addEventListener(
      SLIDESHOW_PAGINATION_NAV_CLICK(this.sectionId),
      this.handlePaginationNavClick.bind(this),
      this.eventOptions,
    )

    this.initLoadingOverlay()

    // Watch for small-desktop breakpoint to handle cursor and nav button functionality
    if (this.slides.length > 1) {
      atBreakpointChange(1024, this.handleWindowResize.bind(this))
      this.handleWindowResize()
    }

    // Swipe navigation
    this.addEventListener(
      SWIPE_NAV_PREV,
      this.goToPreviousSlide.bind(this),
      this.eventOptions,
    )
    this.addEventListener(
      SWIPE_NAV_NEXT,
      this.goToNextSlide.bind(this),
      this.eventOptions,
    )

    // Theme editor functionality
    if (window.Shopify.designMode) {
      this.parentSection = this.closest(this.selectors.parentSection)

      // Block events
      this.parentSection.addEventListener(
        SHOPIFY_BLOCK_SELECT,
        this.handleSlideSelect.bind(this),
        this.eventOptions,
      )
      this.parentSection.addEventListener(
        SHOPIFY_BLOCK_DESELECT,
        this.handleSlideDeselect.bind(this),
        this.eventOptions,
      )

      // When sections are re-ordered in the theme editor, the HTML is preserved, so we need to make sure
      // that the "current" slide is accurate.
      Array.from(this.slides).some((slide) => {
        if (slide.classList.contains(this.classes.currentSlide)) {
          this.currentSlide = slide
          this.currentSlideIndex = slide.getAttribute("data-slide-index")
          return true
        } else {
          return false
        }
      })
    }
  }

  initLoadingOverlay() {
    // Check whether first slide is a video or image
    if (this.slides[0].matches(this.selectors.videoSlide)) {
      const firstSlideVideoContainer = this.slides[0].querySelector(
        this.selectors.videoContainer,
      )

      if (firstSlideVideoContainer) {
        if (firstSlideVideoContainer.children.length > 0) {
          this.hideLoadingOverlay()
        } else {
          const videoSlideObserver = new MutationObserver(
            (mutations, observer) => {
              for (const mutation of mutations) {
                if (mutation.target.children.length > 0) {
                  this.hideLoadingOverlay()
                  observer.disconnect()
                }
              }
            },
          )

          videoSlideObserver.observe(firstSlideVideoContainer, {
            childList: true,
          })
        }
      } else {
        const firstSlidePlaceholderImage = this.slides[0].querySelector(
          this.selectors.videoPlaceholderImage,
        )

        if (
          firstSlidePlaceholderImage.classList.contains(this.classes.visible)
        ) {
          this.hideLoadingOverlay()
        } else {
          const videoSlideObserver = this.createClassMutationObserver(
            this.hideLoadingOverlay.bind(this),
          )

          videoSlideObserver.observe(firstSlidePlaceholderImage, {
            attributeFilter: ["class"],
          })
        }
      }
    } else {
      let firstSlideImage = this.slides[0].querySelector(
        this.selectors.slideImageDesktop,
      )

      if (window.matchMedia(this.breakpoints.mobile).matches) {
        const firstSlideMobileImage = this.slides[0].querySelector(
          this.selectors.slideImageMobile,
        )
        if (firstSlideMobileImage) {
          firstSlideImage = firstSlideMobileImage
        }
      }

      if (firstSlideImage.classList.contains(this.classes.visible)) {
        this.hideLoadingOverlay()
      } else {
        const imageSlideObserver = this.createClassMutationObserver(
          this.hideLoadingOverlay.bind(this),
        )

        imageSlideObserver.observe(firstSlideImage, {
          attributeFilter: ["class"],
        })
      }
    }
  }

  createClassMutationObserver(callback) {
    return new MutationObserver((mutations, observer) => {
      for (const mutation of mutations) {
        if (mutation.target.classList.contains(this.classes.visible)) {
          callback()
          observer.disconnect()
        }
      }
    })
  }

  hideLoadingOverlay() {
    this.changeSlide(this.firstIndexOnLoad)

    if (
      this.slides.length > 1 &&
      this.getAttribute("data-enable-autoplay") === "true"
    ) {
      const startAutoplayEvent = new Event(
        SLIDESHOW_PAGINATION_START_AUTOPLAY(this.sectionId),
      )

      // Wait for pagination element to connect before trying to init
      window.addEventListener(
        SLIDESHOW_PAGINATION_CONNECTED(this.sectionId),
        () => {
          this.initAutoplay()

          // We don't want autoplay to run if a block is selected
          if (!this.blockSelected) {
            window.dispatchEvent(startAutoplayEvent)
          }
        },
        this.eventOptions,
      )
    }

    this.classList.add(this.classes.loaded)
  }

  handlePaginationBulletClick(event) {
    this.changeSlide(event.detail.index)
  }

  handleWindowResize() {
    if (window.matchMedia(this.breakpoints.smallDesktop).matches) {
      this.mobileController?.abort()
      this.swipeNav?.destroy()
      this.initCursor()
    } else {
      this.cursorController?.abort()
      this.widthWatcher?.destroy()
      this.mobileController = new AbortController()
      this.swipeNav.init(this.slideshow)
    }
  }

  initCursor() {
    this.cursor = this.querySelector(this.selectors.cursor)
    this.cursorController = new AbortController()
    this.midpoint = this.offsetWidth / 2
    const cursorEventOptions = { signal: this.cursorController.signal }

    // Track the "midpoint" of the slider so the cursor can update for prev/next
    this.widthWatcher = srraf(({ vw, pvw }) => {
      if (vw === pvw) return
      this.midpoint = this.offsetWidth / 2
    })

    this.slideshow.addEventListener(
      "mouseenter",
      this.showCursor.bind(this),
      cursorEventOptions,
    )

    this.slideshow.addEventListener(
      "mousemove",
      this.updateCursorPosition.bind(this),
      cursorEventOptions,
    )

    this.slideshow.addEventListener(
      "mouseleave",
      this.hideCursor.bind(this),
      cursorEventOptions,
    )

    // Track mouse enter/leave of interactive elements so that we can disable the custom cursor
    this.pagination = this.querySelector(this.selectors.pagination)
    this.slideContentElements = this.querySelectorAll(
      this.selectors.slideContent,
    )

    this.interactiveElementSelectors = `${this.selectors.loadingOverlay}, ${this.selectors.pagination}, ${this.selectors.slideContent}, ${this.selectors.videoPauseButton}`
    const interactiveElements = [this.loadingOverlay, this.pagination]
    this.slideContentElements.forEach((slideContentElement) => {
      interactiveElements.push(slideContentElement)
    })

    interactiveElements.forEach((element) => {
      element.addEventListener(
        "mouseenter",
        this.hideCursor.bind(this),
        cursorEventOptions,
      )
      element.addEventListener(
        "mouseleave",
        this.showCursor.bind(this),
        cursorEventOptions,
      )
    })

    // Handle clicks
    this.slideshow.addEventListener(
      "click",
      this.handleSlideshowClick.bind(this),
      cursorEventOptions,
    )
  }

  showCursor() {
    this.cursor.classList.add(this.classes.active)
  }

  updateCursorPosition(event) {
    /*
     * Update the cursor position
     *
     * We use pageX/pageY because offsetX/offsetY are thrown off by child
     * elements, which means that offsetTop and paddingTop need to be
     * accounted for.
     *
     * We also account for the size of the cursor element so that the element
     * is centered on the actual cursor. The element is 54px wide/tall, so we
     * subtract 27.
     */
    this.cursor.style.top = `${
      event.pageY - 27 - this.offsetTop - this.getAttribute("data-padding-top")
    }px`
    this.cursor.style.left = `${event.pageX - 27 - this.slideshow.offsetLeft}px`

    // Update the cursor display (prev/next)
    if (event.pageX <= this.midpoint) {
      this.cursor.classList.add(this.classes.prevCursor)
      this.cursor.classList.remove(this.classes.nextCursor)
    } else {
      this.cursor.classList.remove(this.classes.prevCursor)
      this.cursor.classList.add(this.classes.nextCursor)
    }
  }

  hideCursor() {
    this.cursor.classList.remove(this.classes.active)
  }

  handleSlideshowClick(event) {
    // If the click wasn't on an interactive element, change slides
    if (!event.target.closest(this.interactiveElementSelectors)) {
      const direction = this.cursor.classList.contains(this.classes.prevCursor)
        ? "prev"
        : "next"

      // Not sure why we need this 40.5 (equivalent to 3/4 ofthe cursor width/height), but
      // it aligns the radial gradient to the cursor
      const irisOriginX =
        ((event.offsetX + 40.5) / this.slideshow.offsetWidth) * 100.0
      const irisOriginY =
        ((event.offsetY + 40.5) / this.slideshow.offsetHeight) * 100.0
      const irisOrigin = `${irisOriginX}% ${irisOriginY}%`

      this.changeSlide(this.getNewSlideIndex(direction), irisOrigin)
    }
  }

  handlePaginationNavClick(event) {
    this.changeSlide(this.getNewSlideIndex(event.detail.direction))
  }

  initAutoplay() {
    window.addEventListener(
      SLIDESHOW_PAGINATION_AUTOPLAY_ADVANCE(this.sectionId),
      () => {
        this.changeSlide(this.getNewSlideIndex("next"))
      },
      this.eventOptions,
    )
  }

  getNewSlideIndex(direction) {
    let newSlideIndex

    if (direction === "prev") {
      if (
        this.currentSlide.getAttribute("data-slide-index") ===
        this.firstSlideIndex
      ) {
        newSlideIndex = this.lastSlideIndex
      } else {
        newSlideIndex = `${parseInt(this.currentSlideIndex, 10) - 1}`
      }
    } else {
      if (
        this.currentSlide.getAttribute("data-slide-index") ===
        this.lastSlideIndex
      ) {
        newSlideIndex = this.firstSlideIndex
      } else {
        newSlideIndex = `${parseInt(this.currentSlideIndex, 10) + 1}`
      }
    }

    return newSlideIndex
  }

  /**
   * Changes the active slide to the given slide index.
   *
   * @param {string} index - The index of the slide that should become active. This is a string as the index is pulled from data
   *                         attributes.
   * @param {string} irisOrigin - The X/Y coordinates (as a percentage) of where the iris animation should start.
   */
  changeSlide(index, irisOrigin = "50% 50%") {
    // If the next slide is the same as the current, don't change
    if (index === this.currentSlideIndex) return

    // Set the iris wipe origin
    this.style.setProperty("--slide-iris-origin", irisOrigin)

    // Remove the transition class from any slides that currently have it
    this.slideList.forEach((slide) => {
      slide.classList.remove(this.classes.slideTransitionOut)
    })

    // Set new "current" slide and handle slide change transition
    const currentSlide = this.currentSlide
    const newSlide = this.slideList.find((slide) => {
      return slide.getAttribute("data-slide-index") === index
    })

    // If the current slide is a video slide, pause the video
    // We timeout for the duration of the slide change so that you don't actually
    //  see the pause
    if (currentSlide.matches(this.selectors.videoSlide)) {
      const video = currentSlide.querySelector("video")

      setTimeout(() => {
        video?.pause()
      }, 1400)
    }

    newSlide.classList.add(this.classes.currentSlide)
    currentSlide.classList.add(this.classes.slideTransitionOut)
    currentSlide.classList.remove(this.classes.currentSlide)
    currentSlide.setAttribute("inert", "")
    this.currentSlide = newSlide
    this.currentSlideIndex = index
    this.currentSlide.removeAttribute("inert")

    if (newSlide.matches(this.selectors.videoSlide)) {
      const video = newSlide.querySelector("video")

      // It's possible that we have a video slide with no video (no video has been selected yet)
      video?.play()
    }

    // Emit event to update pagination display
    const changeActivePaginationBulletEvent = new CustomEvent(
      SLIDESHOW_PAGINATION_SET_ACTIVE_BULLET(this.sectionId),
      { detail: { index } },
    )

    window.dispatchEvent(changeActivePaginationBulletEvent)
  }

  goToPreviousSlide() {
    this.changeSlide(this.getNewSlideIndex("prev"))
  }

  goToNextSlide() {
    this.changeSlide(this.getNewSlideIndex("next"))
  }

  handleSlideSelect(event) {
    this.blockSelected = true

    const selectedSlide = this.querySelector(
      this.selectors.slideByBlockId(event.detail.blockId),
    )
    const pauseAutoplayEvent = new Event(
      SLIDESHOW_PAGINATION_PAUSE_AUTOPLAY(this.sectionId),
    )

    if (this.currentSlide !== selectedSlide) {
      if (
        this.slides.length > 1 &&
        this.getAttribute("data-enable-autoplay") === "true"
      ) {
        window.dispatchEvent(pauseAutoplayEvent)
      }

      this.changeSlide(selectedSlide.getAttribute("data-slide-index"))
    }
  }

  handleSlideDeselect() {
    this.blockSelected = false

    if (
      this.slides.length > 1 &&
      this.getAttribute("data-enable-autoplay") === "true"
    ) {
      const resumeAutoplayEvent = new Event(
        SLIDESHOW_PAGINATION_RESUME_AUTOPLAY(this.sectionId),
      )
      window.dispatchEvent(resumeAutoplayEvent)
    }
  }

  disconnectedCallback() {
    this.controller.abort()
    this.cursorController?.abort()
    this.mobileController?.abort()
    this.swipeNav?.destroy()
    this.widthWatcher?.destroy()
    this.intersectionWatcher.destroy()
  }
}

if (!customElements.get("slideshow-section")) {
  customElements.define("slideshow-section", SlideshowSection)
}
